// utils/sessionTracker.js
// ──────────────────────────────────────────────────────────────────────────────
// Tracks how long a student spends on a learning page.
// Uses visibilitychange + beforeunload to log ONE API call per session,
// instead of periodic heartbeats.
//
// Usage:
//   const tracker = startSession({ activityType: 'video', referenceId, referenceTitle });
//   // on unmount:
//   tracker.end();
// ──────────────────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_URL || '';

// Lazy-import getBaseUrl to get the correct API origin
let _resolvedBase = null;
function getApiBase() {
  if (_resolvedBase !== null) return _resolvedBase;
  try {
    // getBaseUrl returns e.g. "http://localhost:5300/api" — strip trailing /api
    const { getBaseUrl } = require('./getBaseUrl');
    const base = getBaseUrl();
    _resolvedBase = base.replace(/\/api\/?$/, '');
  } catch {
    _resolvedBase = API_BASE;
  }
  return _resolvedBase;
}

/**
 * Start tracking a learning session.
 * @param {{ activityType: string, referenceId?: string, referenceTitle?: string }} opts
 * @returns {{ end: () => void }}
 */
export function startSession({ activityType, referenceId, referenceTitle }) {
  const sessionStart = new Date().toISOString();
  let startTime = Date.now();
  let accumulatedMs = 0;
  let running = true;
  let sent = false;

  // Pause timer when tab goes hidden; resume when visible
  const handleVisibility = () => {
    if (document.hidden) {
      if (running) {
        accumulatedMs += Date.now() - startTime;
        running = false;
      }
    } else {
      if (!running) {
        startTime = Date.now();
        running = true;
      }
    }
  };

  const send = () => {
    if (sent) return;
    if (running) {
      accumulatedMs += Date.now() - startTime;
      running = false;
    }
    const duration = Math.round(accumulatedMs / 1000);
    if (duration < 5) return; // too short to bother

    sent = true;
    const token = localStorage.getItem('token');
    const payload = JSON.stringify({
      activityType,
      referenceId: referenceId || undefined,
      referenceTitle: referenceTitle || undefined,
      duration,
      sessionStart,
    });

    // Use sendBeacon if available (works on tab close); fall back to fetch keepalive
    const url = `${getApiBase()}/api/videos/log-session`;
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      // sendBeacon doesn't support custom headers, so use fetch keepalive
      try {
        fetch(url, { method: 'POST', headers, body: payload, keepalive: true }).catch(() => {});
      } catch {
        // last resort
        navigator.sendBeacon(url, blob);
      }
    } else {
      fetch(url, { method: 'POST', headers, body: payload, keepalive: true }).catch(() => {});
    }
  };

  const handleBeforeUnload = () => send();

  document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('beforeunload', handleBeforeUnload);

  return {
    /** Call on component unmount or navigation away */
    end() {
      send();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    },
  };
}
